import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchAddresses, SearchResponse } from '../api/addressSearch';
import { AddressResult } from '../types';
import { trackSearch, trackSearchQuery, trackVoiceSearch } from '../hooks/useAnalytics';
import { addRecentSearch } from '../utils/recentSearches';
import { useAnimation } from '../hooks/useAnimations';
import RecentSearches from './RecentSearches';
import './AddressSearch.css';

interface AddressSearchProps {
    onAddressSelect: (address: AddressResult) => void;
}

const AddressSearch: React.FC<AddressSearchProps> = ({ onAddressSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<AddressResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [error, setError] = useState<string | null>(null);
    const [addressSelected, setAddressSelected] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isVoiceSupported, setIsVoiceSupported] = useState(false);
    const [voiceLanguage, setVoiceLanguage] = useState<'en-US' | 'zh-CN'>('en-US');
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Animation hooks
    // const searchAnimation = useAnimation({
    //     type: 'fadeInUp',
    //     trigger: 'mount',
    //     duration: 500
    // });

    const resultsAnimation = useAnimation({
        type: 'slideUp',
        trigger: 'manual',
        duration: 300
    });

    // Function to detect if text contains Chinese characters
    const containsChinese = (text: string): boolean => {
        return /[\u4e00-\u9fff]/.test(text);
    };

    // Function to detect language based on input
    const detectLanguage = (text: string): 'en-US' | 'zh-CN' => {
        return containsChinese(text) ? 'zh-CN' : 'en-US';
    };

    // Function to get the best language code for speech recognition
    const getSpeechRecognitionLanguage = (preferredLanguage: 'en-US' | 'zh-CN'): string => {
        if (preferredLanguage === 'zh-CN') {
            // Try different Chinese language codes for better compatibility
            const chineseCodes = ['zh-CN', 'zh-TW', 'zh-HK', 'cmn-CN', 'cmn-TW'];
            return chineseCodes[0]; // Start with zh-CN, can be enhanced to test availability
        }
        return preferredLanguage;
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if voice recognition is supported
    useEffect(() => {
        const checkVoiceSupport = () => {
            const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            setIsVoiceSupported(!!SpeechRecognition);
        };
        
        checkVoiceSupport();
    }, []);

    const performSearch = useCallback(async () => {
        // Don't perform search if an address has been selected
        if (addressSelected) {
            return;
        }

        if (query.length < 2) {
            setResults([]);
            setShowDropdown(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);
        
        try {
            const searchResponse: SearchResponse = await searchAddresses(query);
            
            if (searchResponse.error) {
                // Handle API-level errors
                setError(searchResponse.error);
                setResults([]);
                setShowDropdown(true); // Show dropdown to display error
            } else {
                setResults(searchResponse.results);
                setShowDropdown(searchResponse.results.length > 0);
                setSelectedIndex(-1);
                
                // Trigger results animation
                if (searchResponse.results.length > 0) {
                    setTimeout(() => {
                        resultsAnimation.animate();
                    }, 100);
                }
            }
            
            // Track search event
            trackSearch(query, searchResponse.results.length);
            
            // Track detailed search query information
            trackSearchQuery(query, searchResponse.results.length, searchResponse.results.length > 0);
        } catch (error) {
            // Handle unexpected errors
            console.error('Unexpected search error:', error);
            const errorMessage = error instanceof Error 
                ? error.message 
                : 'An unexpected error occurred. Please try again.';
            setError(errorMessage);
            setResults([]);
            setShowDropdown(true); // Show dropdown to display error
        } finally {
            setIsLoading(false);
        }
    }, [query, addressSelected, resultsAnimation]);

    useEffect(() => {
        const debounceTimer = setTimeout(performSearch, 300);
        return () => clearTimeout(debounceTimer);
    }, [query, addressSelected, performSearch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        // Reset addressSelected when user starts typing again
        if (addressSelected) {
            setAddressSelected(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => 
                prev < results.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && results[selectedIndex]) {
                // If an item is selected from dropdown, use that
                handleAddressSelect(results[selectedIndex]);
            } else if (results.length > 0) {
                // If no item is selected but there are results, use the first result
                handleAddressSelect(results[0]);
            } else if (query.trim()) {
                // If there are no results but user has entered text, 
                // create a mock result and display it
                const mockAddress: AddressResult = {
                    id: 'manual-search',
                    name: query,
                    nameEn: query,
                    nameZh: query,
                    address: query,
                    addressEn: query,
                    addressZh: query,
                    district: '',
                    districtEn: '',
                    districtZh: '',
                    coordinates: {
                        latitude: 22.3193,
                        longitude: 114.1694
                    }
                };
                handleAddressSelect(mockAddress);
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
            setSelectedIndex(-1);
        }
    };

    const handleAddressSelect = (address: AddressResult) => {
        // Add to recent searches
        addRecentSearch(address);
        
        onAddressSelect(address);
        setQuery(address.name);
        setShowDropdown(false);
        setSelectedIndex(-1);
        setAddressSelected(true);
        
        // Remove focus from search input and blur it
        if (inputRef.current) {
            inputRef.current.blur();
        }
        
        // Focus on the results section after a short delay
        setTimeout(() => {
            const resultsSection = document.querySelector('.app-main');
            if (resultsSection) {
                resultsSection.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 100);
    };

    const handleClearSearch = () => {
        setQuery('');
        setResults([]);
        setShowDropdown(false);
        setSelectedIndex(-1);
        setError(null);
        setAddressSelected(false);
        
        // Focus the input after clearing
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
    };

    const startVoiceSearch = () => {
        if (!isVoiceSupported) {
            alert('Voice search is not supported in your browser. Please use Chrome or Safari.');
            return;
        }

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = getSpeechRecognitionLanguage(voiceLanguage);
        
        setIsListening(true);
        
        recognition.onstart = () => {
            setIsListening(true);
        };
        
        recognition.onresult = (event: any) => {
            const transcript = event.results[0][0].transcript;
            console.log('Voice transcript:', transcript);
            setQuery(transcript);
            setIsListening(false);
            
            // Update voice language based on detected content
            const detectedLang = detectLanguage(transcript);
            console.log('Detected language:', detectedLang);
            setVoiceLanguage(detectedLang);
            
            // Track successful voice search
            trackVoiceSearch(true);
            
            // Reset addressSelected to allow new search
            setAddressSelected(false);
        };
        
        recognition.onerror = (event: any) => {
            console.error('Speech recognition error:', event.error);
            setIsListening(false);
            
            // Track failed voice search
            trackVoiceSearch(false, event.error);
            
            let errorMessage = 'Voice recognition failed. Please try again.';
            if (event.error === 'not-allowed') {
                errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
            } else if (event.error === 'no-speech') {
                errorMessage = 'No speech detected. Please speak clearly and try again.';
            } else if (event.error === 'audio-capture') {
                errorMessage = 'Audio capture failed. Please check your microphone and try again.';
            } else if (event.error === 'language-not-supported') {
                errorMessage = 'Language not supported. Please try switching languages or use English.';
            }
            
            alert(errorMessage);
        };
        
        recognition.onend = () => {
            setIsListening(false);
        };
        
        recognition.start();
    };

    const toggleVoiceLanguage = () => {
        const newLanguage = voiceLanguage === 'en-US' ? 'zh-CN' : 'en-US';
        setVoiceLanguage(newLanguage);
    };



    return (
        <div className="address-search animate-fade-in-up" ref={searchRef}>
            <div className={`search-container ${addressSelected ? 'address-selected' : ''}`}>

                
                <div className="search-input-wrapper">
                    <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="m21 21-4.35-4.35"/>
                    </svg>
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Search for an address in Hong Kong..."
                        className={`search-input ${addressSelected ? 'address-selected' : ''}`}
                        autoComplete="off"
                    />
                    {isVoiceSupported && (
                        <div className="voice-controls">
                            <button
                                type="button"
                                className={`voice-button ${isListening ? 'listening' : ''}`}
                                onClick={startVoiceSearch}
                                disabled={isListening}
                                aria-label="Voice search"
                                title={`Voice search (${voiceLanguage === 'zh-CN' ? 'Chinese' : 'English'})`}
                            >
                                <svg className="voice-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                                    <line x1="12" y1="19" x2="12" y2="23"/>
                                    <line x1="8" y1="23" x2="16" y2="23"/>
                                </svg>
                                {isListening && (
                                    <div className="voice-language-indicator">
                                        {voiceLanguage === 'zh-CN' ? '中' : 'EN'}
                                    </div>
                                )}
                            </button>
                            <button
                                type="button"
                                className="language-toggle"
                                onClick={toggleVoiceLanguage}
                                disabled={isListening}
                                aria-label={`Switch to ${voiceLanguage === 'zh-CN' ? 'English' : 'Chinese'} voice recognition`}
                                title={`Switch to ${voiceLanguage === 'zh-CN' ? 'English' : 'Chinese'} voice recognition`}
                            >
                                {voiceLanguage === 'zh-CN' ? 'EN' : '中'}
                            </button>
                        </div>
                    )}
                    {isLoading && (
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                        </div>
                    )}
                    {query && !isLoading && (
                        <button
                            type="button"
                            className="clear-button"
                            onClick={handleClearSearch}
                            aria-label="Clear search"
                        >
                            <svg className="clear-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="m15 9-6 6"/>
                                <path d="m9 9 6 6"/>
                            </svg>
                        </button>
                    )}
                </div>

                {error && (
                    <div className="error-message">
                        <div className="error-content">
                            <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <div className="error-text">
                                <p className="error-title">Search failed</p>
                                <p className="error-description">{error}</p>
                            </div>
                            <button 
                                className="retry-button" 
                                onClick={() => {
                                    setError(null);
                                    if (query.length >= 2) {
                                        performSearch();
                                    }
                                }}
                                aria-label="Retry search"
                            >
                                <svg className="retry-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                                    <path d="M21 3v5h-5"/>
                                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                                    <path d="M3 21v-5h5"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                )}



                {showDropdown && (
                    <div className="search-dropdown animate-slide-up" ref={resultsAnimation.elementRef as React.RefObject<HTMLDivElement>}>
                        {isLoading ? (
                            <div className="loading-state">
                                <div className="loading-spinner">
                                    <div className="spinner"></div>
                                </div>
                                <p>Searching addresses...</p>
                            </div>
                        ) : results.length === 0 ? (
                            <div className="no-results">
                                <p>No addresses found.</p>
                                <p className="no-results-hint">Try a different search term</p>
                            </div>
                        ) : (
                            <ul className="results-list">
                                {results.map((result, index) => (
                                    <li
                                        key={result.id}
                                        className={`result-item animate-fade-in-up ${index === selectedIndex ? 'selected' : ''}`}
                                        style={{ animationDelay: `${index * 50}ms` }}
                                        onClick={() => handleAddressSelect(result)}
                                    >
                                        <div className="result-content">
                                            <div className="result-name">
                                                {result.nameEn}
                                            </div>
                                            <div className="result-address">
                                                {result.addressEn}
                                            </div>
                                            <div className="result-name-zh">
                                                {result.nameZh}
                                            </div>
                                            <div className="result-address-zh">
                                                {result.addressZh}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
            
            {/* Recent Searches Component */}
            <RecentSearches 
                onAddressSelect={handleAddressSelect}
                isVisible={!showDropdown && !addressSelected}
            />
        </div>
    );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(AddressSearch); 